// src/pages/products/Products.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import StorageImage from '../../shared/StorageImage';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [updatingProduct, setUpdatingProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, products, showOnlyActive]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'products'), orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      const productsData = [];
      querySnapshot.forEach((doc) => {
        productsData.push({ id: doc.id, ...doc.data() });
      });
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        (product.nombre || '').toLowerCase().includes(term) ||
        (product.descripción || '').toLowerCase().includes(term) ||
        (product.categoría || '').toLowerCase().includes(term)
      );
    }
    
    // Filter by active status
    if (showOnlyActive) {
      filtered = filtered.filter(product => product.active === true);
    }
    
    setFilteredProducts(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateProduct = () => {
    navigate('/admin/products/create');
  };

  const handleEditProduct = (id) => {
    navigate(`/admin/products/${id}`);
  };

  const handleDeleteDialogOpen = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await deleteDoc(doc(db, 'products', productToDelete.id));
      setProducts(products.filter(item => item.id !== productToDelete.id));
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleToggleActive = async (product) => {
    setUpdatingProduct(product.id);
    try {
      const newActiveStatus = !product.active;
      
      await updateDoc(doc(db, 'products', product.id), {
        active: newActiveStatus
      });
      
      // Update local state
      setProducts(products.map(item => 
        item.id === product.id ? { ...item, active: newActiveStatus } : item
      ));
    } catch (error) {
      console.error('Error updating product status:', error);
    } finally {
      setUpdatingProduct(null);
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'uniforme':
        return 'Uniforme';
      case 'material':
        return 'Material';
      case 'libros':
        return 'Libros';
      default:
        return 'Otros';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Productos</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateProduct}
        >
          Nuevo Producto
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="outlined"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyActive}
                onChange={(e) => setShowOnlyActive(e.target.checked)}
                color="primary"
              />
            }
            label="Solo activos"
            sx={{ ml: 2 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="center">Stock</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Destacado</TableCell>
                <TableCell align="right">Acciones</TableCell>
                <TableCell align="center">PayPal</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No se encontraron productos
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {product.imágenes && product.imágenes.length > 0 ? (
                            <Avatar variant="rounded" sx={{ mr: 2, overflow: 'hidden', bgcolor: 'transparent' }}>
                              <StorageImage src={product.imágenes[0]} alt={product.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Avatar>
                          ) : (
                            <Avatar variant="rounded" sx={{ mr: 2, bgcolor: 'grey.300' }}>
                              No img
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="subtitle2">{product.nombre}</Typography>
                            <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                              {product.descripción || 'Sin descripción'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getCategoryName(product.categoría)}
                      </TableCell>
                      <TableCell align="right">
                        {product.precio?.toFixed(2) || '0.00'} €
                      </TableCell>
                      <TableCell align="center">
                        {product.stock === 0 ? (
                          <Chip label="Agotado" color="error" size="small" />
                        ) : product.stock <= 5 ? (
                          <Chip label={`${product.stock} uds.`} color="warning" size="small" />
                        ) : (
                          product.stock
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={product.active || false}
                              onChange={() => handleToggleActive(product)}
                              disabled={updatingProduct === product.id}
                              size="small"
                            />
                          }
                          label={product.active ? "Activo" : "Inactivo"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {product.destacado ? (
                          <Chip label="Destacado" color="primary" size="small" />
                        ) : (
                          <Chip label="No" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton 
                            onClick={() => handleEditProduct(product.id)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton 
                            onClick={() => handleDeleteDialogOpen(product)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {product.paypalLink ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => {
                              if (product.paypalLink && product.paypalLink.startsWith('https://')) {
                                window.open(product.paypalLink, '_blank');
                              }
                            }}
                          >
                            Ver enlace
                          </Button>
                        ) : (
                          <Typography variant="body2" color="textSecondary">Sin enlace</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>¿Eliminar producto?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar el producto "{productToDelete?.nombre}"? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button onClick={handleDeleteProduct} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;